import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';

import {
  useLiveGameLolQuery,
  useLookupLolQuery,
} from '@/services/qriotsApi';
import type { Platform } from '@/types/qriots';
import { classifyQriotsError } from '@/services/qriotsErrors';
import { useIsVisible } from '@/hooks/useIsVisible';
import { Loader } from '@/components/common/Loader';
import { LolProfileCard } from '@/components/riot/LolProfileCard';
import { LiveGameBanner } from '@/components/riot/LiveGameBanner';
import { QriotsErrorState } from '@/components/riot/QriotsErrorState';
import { SaveAccountButton } from '@/components/riot/SaveAccountButton';

export function RiotLolDetail() {
  const { t } = useTranslation('riot');
  const { puuid = '' } = useParams<{ puuid: string }>();
  const [search] = useSearchParams();
  const region = (search.get('region') ?? 'EUW1') as Platform;
  const gameName = search.get('gameName') ?? '';
  const tagLine = search.get('tagLine') ?? '';

  const visible = useIsVisible();

  const profile = useLookupLolQuery(
    { gameName, tagLine, region },
    { skip: !gameName || !tagLine },
  );

  const liveGame = useLiveGameLolQuery(
    { puuid, region },
    { pollingInterval: 60_000, skip: !puuid || !visible },
  );

  // not_in_game = 404 = "not playing right now", which we want to render as
  // "no banner" rather than as an error.
  const liveGameError = liveGame.error
    ? classifyQriotsError(liveGame.error)
    : null;
  const liveGameIsNotPlaying = liveGameError?.kind === 'not_found';

  if (profile.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (profile.error) {
    const { kind, retryAfterSec } = classifyQriotsError(profile.error);
    return (
      <QriotsErrorState
        kind={kind}
        retryAfterSec={retryAfterSec}
        onRetry={profile.refetch}
      />
    );
  }

  if (!profile.data) return null;

  return (
    <div className="flex flex-col gap-4 pb-6 pt-4">
      {liveGame.data && !liveGameIsNotPlaying && (
        <LiveGameBanner data={liveGame.data} viewerPuuid={puuid} />
      )}
      <LolProfileCard data={profile.data} />
      {puuid && gameName && tagLine && (
        <SaveAccountButton
          puuid={puuid}
          game="lol"
          region={region}
          gameName={gameName}
          tagLine={tagLine}
        />
      )}
      <nav className="mx-4 flex flex-col gap-2">
        <Link
          to={`/riot/lol/${encodeURIComponent(puuid)}/matches?region=${region}`}
          className="flex items-center justify-between rounded-lg bg-bg-tertiary px-3 py-2.5 text-sm font-medium text-fg transition-opacity active:opacity-80"
        >
          <span>{t('cta.viewAllMatches')}</span>
          <ChevronRight aria-hidden className="h-4 w-4 text-fg-muted" />
        </Link>
        <Link
          to={`/riot/lol/${encodeURIComponent(puuid)}/stats?region=${region}`}
          className="flex items-center justify-between rounded-lg bg-bg-tertiary px-3 py-2.5 text-sm font-medium text-fg transition-opacity active:opacity-80"
        >
          <span>{t('cta.viewStats')}</span>
          <ChevronRight aria-hidden className="h-4 w-4 text-fg-muted" />
        </Link>
        <Link
          to={`/riot/lol/${encodeURIComponent(puuid)}/mastery?region=${region}`}
          className="flex items-center justify-between rounded-lg bg-bg-tertiary px-3 py-2.5 text-sm font-medium text-fg transition-opacity active:opacity-80"
        >
          <span>{t('cta.viewMastery')}</span>
          <ChevronRight aria-hidden className="h-4 w-4 text-fg-muted" />
        </Link>
      </nav>
    </div>
  );
}
